import type { Field } from '@lucidtech/las-sdk-browser';

import {
  FORMATTER_ADDRESS_FUNCTION_ID,
  FORMATTER_CLASSIFICATION_FUNCTION_ID,
  FORMATTER_COUNTRY_FUNCTION_ID,
  FORMATTER_CURRENCY_FUNCTION_ID,
  FORMATTER_CUSTOM_CODE_FUNCTION_ID,
  FORMATTER_DATE_FUNCTION_ID,
  FORMATTER_DATE_TIME_FUNCTION_ID,
  FORMATTER_NUMERIC_FUNCTION_ID,
  FORMATTER_REGEX_FUNCTION_ID,
  FORMATTER_SOCIAL_SECURITY_NUMBER_FUNCTION_ID,
  FORMATTER_TIME_FUNCTION_ID,
  VALIDATOR_BIC_SWIFT_FUNCTION_ID,
  VALIDATOR_EMAIL_FUNCTION_ID,
  VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID,
  VALIDATOR_IBAN_FUNCTION_ID,
  VALIDATOR_LICENSE_PLATE_FUNCTION_ID,
  VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID,
  VALIDATOR_PHONE_NUMBER_FUNCTION_ID,
  VALIDATOR_TAX_ID_FUNCTION_ID,
  VALIDATOR_URL_FUNCTION_ID,
  VALIDATOR_VAT_ID_FUNCTION_ID
} from '@/constants';
import { FieldState } from '@/store';

export const findField = (fields: FieldState[], id: string) => {
  for (const field of fields) {
    if (field.id === id) {
      return field;
    }
    if (field.fields) {
      for (const subField of field.fields) {
        if (subField.id === id) {
          return subField;
        }
      }
    }
  }
};

export type FieldOrFieldState = FieldState | Field;

export const isDateField = (field: FieldOrFieldState) => {
  return Boolean(
    field.formatters?.find((f) => [FORMATTER_DATE_FUNCTION_ID, FORMATTER_DATE_TIME_FUNCTION_ID].includes(f.functionId))
  );
};

export const isNumericField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_NUMERIC_FUNCTION_ID].includes(f.functionId)));
};

export const isClassificationField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_CLASSIFICATION_FUNCTION_ID].includes(f.functionId)));
};

export const isIBANNumberField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_IBAN_FUNCTION_ID].includes(f.functionId)));
};

export const isRegexField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_REGEX_FUNCTION_ID].includes(f.functionId)));
};

export const isEmailField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_EMAIL_FUNCTION_ID].includes(f.functionId)));
};

export const isCustomCodeField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_CUSTOM_CODE_FUNCTION_ID].includes(f.functionId)));
};

export const isTimeField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_TIME_FUNCTION_ID].includes(f.functionId)));
};

export const isSocialSecurityNumberField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_SOCIAL_SECURITY_NUMBER_FUNCTION_ID].includes(f.functionId)));
};

export const isDateTimeField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_DATE_TIME_FUNCTION_ID].includes(f.functionId)));
};

export const isCurrencyField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_CURRENCY_FUNCTION_ID].includes(f.functionId)));
};

export const isAddressField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_ADDRESS_FUNCTION_ID].includes(f.functionId)));
};

export const isCountryCodeField = (field: FieldOrFieldState) => {
  return Boolean(field.formatters?.find((f) => [FORMATTER_COUNTRY_FUNCTION_ID].includes(f.functionId)));
};

export const isPhoneNumberField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_PHONE_NUMBER_FUNCTION_ID].includes(f.functionId)));
};

export const isBicSwiftField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_BIC_SWIFT_FUNCTION_ID].includes(f.functionId)));
};

export const isVatIdField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_VAT_ID_FUNCTION_ID].includes(f.functionId)));
};

export const isTaxIdField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_TAX_ID_FUNCTION_ID].includes(f.functionId)));
};

export const isUrlField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_URL_FUNCTION_ID].includes(f.functionId)));
};

export const isPassportNumberField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID].includes(f.functionId)));
};

export const isLicensePlateField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_LICENSE_PLATE_FUNCTION_ID].includes(f.functionId)));
};

export const isFreightContainerField = (field: FieldOrFieldState) => {
  return Boolean(field.validators?.find((f) => [VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID].includes(f.functionId)));
};

export const isMultiLineField = (field: FieldOrFieldState) => {
  return !(isClassificationField(field) || isNumericField(field) || isDateField(field));
};
